import {
	ChevronDown,
	ChevronRight,
	FileEdit,
	GripVertical,
	Info,
	X,
} from "lucide-react";
import React, { useState, useMemo, useRef, useEffect } from "react";
import {
	REVIEW_BOARD_MAX_WIDTH,
	REVIEW_BOARD_MIN_WIDTH,
} from "../hooks/useReviewBoardLayout";
import { ReviewBoardCard } from "./ReviewBoardCard";
import { Tooltip } from "./Tooltip";

export const COLUMN_DRAG_TYPE = "application/x-rb-column";

export const ReviewBoardColumn = ({
	column,
	width,
	onResize,
	onRemove,
	onDragStart,
	onDragEnd,
	onDragOver,
	onDrop,
	isDragTarget,
	draggingMR,
	collapsible = false,
	draggingFromIgnored = false,
	greyOutBlockingComments = true,
	enableCommentsExpansion = false,
	// Column reorder
	draggingColumnId = null,
	isColumnDropTarget = false,
	columnDropEdge = "before",
	onColumnDragStart,
	onColumnDragEnd,
	onColumnDragOver,
	onColumnDrop,
	onOpenDiff,
	searchQuery = "",
	getMRTags = () => [],
	allMRTags = [],
	onAddMRTag,
	onRemoveMRTag,
}) => {
	const [isCollapsed, setIsCollapsed] = useState(collapsible);
	// Live width while dragging the resize handle; persisted only on mouse up.
	const [liveWidth, setLiveWidth] = useState(null);
	const resizeStateRef = useRef(null);

	const clampWidth = (w) =>
		Math.max(REVIEW_BOARD_MIN_WIDTH, Math.min(REVIEW_BOARD_MAX_WIDTH, w));

	// Ensure resize listeners are torn down if the column unmounts mid-drag.
	useEffect(() => {
		return () => {
			if (resizeStateRef.current?.cleanup) {
				resizeStateRef.current.cleanup();
			}
		};
	}, []);

	// Allow drop if column accepts drops OR if dragging from ignored column (to unignore)
	const canAcceptDrop =
		column.acceptsDrop || (draggingFromIgnored && column.id !== "ignored");

	const isReordering = !!draggingColumnId;

	const handleDragOver = (e) => {
		if (isReordering) {
			e.preventDefault();
			if (onColumnDragOver) {
				const rect = e.currentTarget.getBoundingClientRect();
				const edge =
					e.clientX < rect.left + rect.width / 2 ? "before" : "after";
				onColumnDragOver(column.id, edge);
			}
			return;
		}
		if (canAcceptDrop) {
			e.preventDefault();
			if (onDragOver) onDragOver(e);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		if (isReordering) {
			if (onColumnDrop) onColumnDrop(column.id);
			return;
		}
		if (canAcceptDrop && onDrop) {
			const mrId = e.dataTransfer.getData("text/plain");
			onDrop(mrId);
		}
	};

	const handleHeaderDragStart = (e) => {
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData(COLUMN_DRAG_TYPE, column.id);
		// Some browsers require text/plain to be set for drag to initiate.
		e.dataTransfer.setData("text/plain", `column:${column.id}`);
		if (onColumnDragStart) onColumnDragStart(column.id);
	};

	const toggleCollapsed = () => {
		if (collapsible) setIsCollapsed((prev) => !prev);
	};

	const handleHeaderKeyDown = (event) => {
		if (!collapsible) return;
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		toggleCollapsed();
	};

	// When dragging cards, expand the column if it can accept drops
	const showExpanded = !isCollapsed || (isDragTarget && canAcceptDrop);

	const startResize = (e) => {
		e.preventDefault();
		e.stopPropagation();

		const startX = e.clientX;
		const startWidth = width;
		document.body.style.userSelect = "none";
		document.body.style.cursor = "col-resize";

		// Update only transient local state during the drag (no localStorage
		// writes per pixel); persist the final width once on mouse up.
		const handleMove = (moveEvent) => {
			const delta = moveEvent.clientX - startX;
			setLiveWidth(clampWidth(startWidth + delta));
		};

		const cleanup = () => {
			document.body.style.userSelect = "";
			document.body.style.cursor = "";
			window.removeEventListener("mousemove", handleMove);
			window.removeEventListener("mouseup", handleUp);
			resizeStateRef.current = null;
		};

		const handleUp = (upEvent) => {
			const delta = upEvent.clientX - startX;
			if (onResize) onResize(column.id, clampWidth(startWidth + delta));
			setLiveWidth(null);
			cleanup();
		};

		resizeStateRef.current = { cleanup };
		window.addEventListener("mousemove", handleMove);
		window.addEventListener("mouseup", handleUp);
	};

	// Separate MRs into non-drafts and drafts
	const { nonDraftMRs, draftMRs } = useMemo(() => {
		const nonDrafts = column.mrs.filter((mr) => !mr.draft);
		const drafts = column.mrs.filter((mr) => mr.draft);
		return { nonDraftMRs: nonDrafts, draftMRs: drafts };
	}, [column.mrs]);

	const effectiveWidth = liveWidth ?? width;
	const containerStyle = showExpanded
		? { width: `${effectiveWidth}px` }
		: undefined;

	return (
		<div
			className={`relative flex flex-col flex-shrink-0 transition-[opacity] duration-200 ${
				showExpanded ? "" : "w-14"
			} ${draggingColumnId === column.id ? "opacity-40" : ""}`}
			style={containerStyle}
			onDragOver={handleDragOver}
			onDrop={handleDrop}
		>
			{/* Lane reorder insertion indicator */}
			{isColumnDropTarget && (
				<div
					className={`absolute top-0 bottom-0 z-30 flex flex-col items-center pointer-events-none ${
						columnDropEdge === "before" ? "-left-2" : "-right-2"
					}`}
				>
					<span className="w-1.5 h-1.5 -mb-0.5 rounded-full bg-[var(--color-accent)]" />
					<span className="flex-1 w-1 rounded-full bg-[var(--color-accent)]" />
					<span className="w-1.5 h-1.5 -mt-0.5 rounded-full bg-[var(--color-accent)]" />
				</div>
			)}

			{/* Column header */}
			<div
				className={`rounded-t-lg px-3 py-2 text-white font-semibold ${collapsible ? "cursor-pointer" : ""}`}
				style={{ backgroundColor: column.color }}
				onClick={collapsible ? toggleCollapsed : undefined}
				onKeyDown={collapsible ? handleHeaderKeyDown : undefined}
				role={collapsible ? "button" : undefined}
				tabIndex={collapsible ? 0 : undefined}
			>
				{showExpanded ? (
					<div className="flex items-center justify-between gap-1">
						<div className="flex items-center gap-1 min-w-0">
							{/* Reorder grip */}
							<button
								type="button"
								draggable
								onDragStart={handleHeaderDragStart}
								onDragEnd={onColumnDragEnd}
								onClick={(e) => e.stopPropagation()}
								className="no-hover-lift -ml-1 flex-shrink-0 cursor-grab rounded opacity-70 hover:opacity-100 active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-white/60"
								title="Drag to reorder lane"
								aria-label={`Drag to reorder ${column.title}`}
							>
								<GripVertical className="w-4 h-4" />
							</button>
							{collapsible && <ChevronDown className="w-4 h-4 flex-shrink-0" />}
							<span className="truncate">{column.title}</span>
							{column.description && (
								<Tooltip content={column.description}>
									<Info className="w-3.5 h-3.5 opacity-70 hover:opacity-100 cursor-help flex-shrink-0" />
								</Tooltip>
							)}
						</div>
						<div className="flex items-center gap-1 flex-shrink-0">
							<span className="bg-white/20 px-2 py-0.5 rounded text-sm">
								{column.mrs.length}
							</span>
							{onRemove && (
								<Tooltip content="Remove lane">
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											onRemove(column.id);
										}}
										className="p-0.5 rounded hover:bg-white/20 transition-colors"
										aria-label={`Remove ${column.title} lane`}
									>
										<X className="w-4 h-4" />
									</button>
								</Tooltip>
							)}
						</div>
					</div>
				) : (
					<Tooltip
						content={
							column.acceptsDrop
								? `${column.description} - drag MRs here`
								: column.description || column.title
						}
					>
						<div className="flex flex-col items-center gap-2">
							<ChevronRight className="w-4 h-4" />
							<span className="bg-white/20 px-2 py-0.5 rounded text-sm">
								{column.mrs.length}
							</span>
							<span
								className="text-xs"
								style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
							>
								{column.title}
							</span>
							{column.acceptsDrop && (
								<GripVertical className="w-4 h-4 opacity-50" />
							)}
						</div>
					</Tooltip>
				)}
			</div>

			{/* Column body */}
			{showExpanded ? (
				<div
					className={`
						flex-1 bg-[var(--color-bg-primary)] rounded-b-lg p-2 
						border border-t-0 border-[var(--color-border)]
						overflow-y-auto
						min-h-[200px] max-h-[calc(100vh-200px)]
						transition-all duration-200
						${isDragTarget && canAcceptDrop ? "ring-2 ring-[var(--color-accent)] ring-inset bg-[var(--color-accent-muted)]" : ""}
					`}
				>
					{/* Card drop placeholder: shows where the dragged MR will land */}
					{draggingMR && isDragTarget && canAcceptDrop && (
						<div className="mb-3 rounded-lg border-2 border-dashed border-[var(--color-accent)] bg-[var(--color-accent)]/10 p-4 text-center text-xs font-medium text-[var(--color-accent)]">
							{column.id === "ignored"
								? "Drop here to ignore this MR"
								: "Drop here to restore this MR"}
						</div>
					)}

					{column.mrs.length === 0 ? (
						!(draggingMR && isDragTarget && canAcceptDrop) && (
							<div className="flex items-center justify-center h-full text-[var(--color-text-secondary)] text-sm p-4">
								{column.acceptsDrop ? (
									<span className="text-center">
										Drag MRs here to ignore them
									</span>
								) : (
									<span>No merge requests</span>
								)}
							</div>
						)
					) : (
						<>
							{/* Non-draft MRs */}
							{nonDraftMRs.map((mr) => (
								<ReviewBoardCard
									key={mr.id}
									mr={mr}
									onDragStart={onDragStart}
									onDragEnd={onDragEnd}
									isDragging={draggingMR?.id === mr.id}
									greyOutBlockingComments={greyOutBlockingComments}
									enableCommentsExpansion={enableCommentsExpansion}
									onOpenDiff={onOpenDiff}
									searchQuery={searchQuery}
									tags={getMRTags(mr)}
									allTags={allMRTags}
									onAddTag={onAddMRTag}
									onRemoveTag={onRemoveMRTag}
								/>
							))}

							{/* Drafts divider */}
							{draftMRs.length > 0 && (
								<div className="flex items-center gap-2 my-3 px-2">
									<div className="flex-1 h-px bg-[var(--color-text-secondary)]" />
									<span className="flex items-center gap-1 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
										<FileEdit className="w-3 h-3" />
										Drafts ({draftMRs.length})
									</span>
									<div className="flex-1 h-px bg-[var(--color-text-secondary)]" />
								</div>
							)}

							{/* Draft MRs */}
							{draftMRs.map((mr) => (
								<ReviewBoardCard
									key={mr.id}
									mr={mr}
									onDragStart={onDragStart}
									onDragEnd={onDragEnd}
									isDragging={draggingMR?.id === mr.id}
									greyOutBlockingComments={greyOutBlockingComments}
									enableCommentsExpansion={enableCommentsExpansion}
									onOpenDiff={onOpenDiff}
									searchQuery={searchQuery}
									tags={getMRTags(mr)}
									allTags={allMRTags}
									onAddTag={onAddMRTag}
									onRemoveTag={onRemoveMRTag}
								/>
							))}
						</>
					)}
				</div>
			) : (
				<div
					className={`
						flex-1 bg-[var(--color-bg-primary)] rounded-b-lg
						border border-t-0 border-[var(--color-border)]
						min-h-[200px] max-h-[calc(100vh-200px)]
						transition-all duration-200
						${isDragTarget && canAcceptDrop ? "ring-2 ring-[var(--color-accent)] ring-inset bg-[var(--color-accent-muted)]" : ""}
					`}
				/>
			)}

			{/* Resize handle with a subtle always-visible grip marker */}
			{showExpanded && onResize && (
				<div
					onMouseDown={startResize}
					className="absolute top-0 -right-1.5 h-full w-3 cursor-col-resize group flex items-center justify-center"
					title="Drag to resize lane"
				>
					{/* Full-height line, faint at rest, accent on hover */}
					<div className="absolute top-2 bottom-2 right-[5px] w-px bg-[var(--color-border)] group-hover:bg-[var(--color-accent)] transition-colors" />
					{/* Centered grip pill marker */}
					<div className="relative z-10 h-8 w-1 rounded-full bg-[var(--color-border)] group-hover:bg-[var(--color-accent)] group-hover:h-12 transition-all duration-150" />
				</div>
			)}
		</div>
	);
};
